import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AppButton, AppCard, AppField, AppInput, AppTextarea, ConfirmAction,
} from "../../../shared/components/ui";
import { PageHeader } from "../../../shared/components/PageChrome";
import { IconCheck, IconSpinner } from "../../../shared/components/icons";
import { api } from "../../../shared/api/client";
import { readEntityPrefill } from "../../../shared/navigation/entityPrefill";
import { isValidTzMobile, normalizeTzPhone } from "../../../shared/phone";

type Template = { name: string; template: string };
type SendState = "idle" | "sending" | "success" | "error";
type CustomerOpt = {
  id: number;
  customer_number: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  phone: string;
};

function templateLabel(name: string): string {
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function customerLabel(c: CustomerOpt): string {
  const name = c.name
    || [c.first_name, c.last_name].filter(Boolean).join(" ")
    || c.customer_number;
  return `${name} — ${c.phone}`;
}

export function SmsCenter() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPrefill = useMemo(() => readEntityPrefill(searchParams), [searchParams]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [customers, setCustomers] = useState<CustomerOpt[]>([]);

  const [singlePhone, setSinglePhone] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [singleMessage, setSingleMessage] = useState("");
  const [singleState, setSingleState] = useState<SendState>("idle");
  const [singleError, setSingleError] = useState("");

  const [bulkPhones, setBulkPhones] = useState("");
  const [bulkMessage, setBulkMessage] = useState("");
  const [bulkState, setBulkState] = useState<SendState>("idle");
  const [bulkError, setBulkError] = useState("");
  const [bulkResult, setBulkResult] = useState<{ sent: number; failed: number } | null>(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [selectedBulkIds, setSelectedBulkIds] = useState<number[]>([]);

  useEffect(() => {
    api.get<{ templates: Template[] }>("/notifications/templates")
      .then((d) => setTemplates(d.templates ?? []))
      .catch(() => setTemplates([]));
    api.get<{ customers?: CustomerOpt[]; data?: CustomerOpt[] }>("/customers?per_page=200&is_active=true")
      .then((d) => setCustomers(d.customers || d.data || []))
      .catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    if (!urlPrefill.customer_id || customers.length === 0) return;
    const c = customers.find((x) => String(x.id) === String(urlPrefill.customer_id));
    if (!c) return;
    setSelectedCustomerId(String(c.id));
    if (c.phone) setSinglePhone(normalizeTzPhone(c.phone));
  }, [urlPrefill.customer_id, customers]);

  const applyTemplate = (t: Template, target: "single" | "bulk") => {
    if (target === "single") setSingleMessage(t.template);
    else setBulkMessage(t.template);
  };

  const sendSingle = async (e: React.FormEvent) => {
    e.preventDefault();
    const phone = normalizeTzPhone(singlePhone);
    if (!phone || !singleMessage.trim()) {
      setSingleState("error");
      setSingleError("Select or enter a phone number and a message");
      return;
    }
    if (!isValidTzMobile(phone)) {
      setSingleState("error");
      setSingleError("Enter a valid Tanzania mobile number in +255XXXXXXXXX format");
      return;
    }
    setSingleState("sending");
    setSingleError("");
    try {
      const res = await api.post<{ success: boolean; error?: string }>("/notifications/send-sms", {
        phone,
        message: singleMessage.trim(),
      });
      if (res.success) {
        setSingleState("success");
      } else {
        setSingleState("error");
        setSingleError(res.error ?? "Message was not delivered");
      }
    } catch (ex: unknown) {
      setSingleState("error");
      setSingleError(ex instanceof Error ? ex.message : "Failed to send message");
    }
  };

  const pickerPhones = useMemo(() => {
    const set = new Set<string>();
    for (const id of selectedBulkIds) {
      const c = customers.find((x) => x.id === id);
      if (c?.phone) set.add(normalizeTzPhone(c.phone));
    }
    return [...set].filter(Boolean);
  }, [selectedBulkIds, customers]);

  const bulkPhoneCandidates = useMemo(() => {
    const typed = bulkPhones
      .split(/\r?\n/)
      .map((p) => normalizeTzPhone(p))
      .filter(Boolean);
    return [...new Set([...pickerPhones, ...typed])];
  }, [bulkPhones, pickerPhones]);
  const invalidBulkPhones = useMemo(
    () => bulkPhoneCandidates.filter((phone) => !isValidTzMobile(phone)),
    [bulkPhoneCandidates],
  );
  const bulkPhoneList = useMemo(
    () => bulkPhoneCandidates.filter(isValidTzMobile),
    [bulkPhoneCandidates],
  );

  const toggleBulkCustomer = (id: number) => {
    setSelectedBulkIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const sendBulk = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (bulkPhoneList.length === 0 || !bulkMessage.trim()) {
      setBulkState("error");
      setBulkError("Pick customers or paste phone numbers, and enter a message");
      return;
    }
    if (invalidBulkPhones.length > 0) {
      setBulkState("error");
      setBulkError(`Invalid Tanzania mobile number${invalidBulkPhones.length === 1 ? "" : "s"}: ${invalidBulkPhones.join(", ")}`);
      return;
    }
    setConfirmBulk(false);
    setBulkState("sending");
    setBulkError("");
    setBulkResult(null);
    try {
      const res = await api.post<{ sent: number; failed: number }>(
        "/notifications/send-bulk-sms",
        { recipients: bulkPhoneList, message: bulkMessage.trim() },
      );
      setBulkResult({ sent: res.sent, failed: res.failed });
      setBulkState("success");
    } catch (ex: unknown) {
      setBulkState("error");
      setBulkError(ex instanceof Error ? ex.message : "Bulk send failed");
    }
  };

  return (
    <div>
      <PageHeader
        title="SMS Center"
        subtitle="Send individual or bulk SMS messages."
        actions={
          <AppButton type="button" variant="secondary" onClick={() => navigate("/notifications")}>
            Notification Log
          </AppButton>
        }
      />

      {templates.length > 0 && (
        <AppCard title="Templates">
          <div className="pg-filter-chips">
            {templates.map((t) => (
              <button
                key={t.name}
                type="button"
                className="pg-chip"
                onClick={() => applyTemplate(t, "single")}
                title={t.template}
              >
                {templateLabel(t.name)}
              </button>
            ))}
          </div>
        </AppCard>
      )}

      <AppCard title="Send to one customer">
        <form onSubmit={sendSingle}>
          <AppField label="Customer">
            <select
              className="pg-select"
              value={selectedCustomerId}
              onChange={(e) => {
                const id = e.target.value;
                setSelectedCustomerId(id);
                const c = customers.find((x) => String(x.id) === id);
                if (c?.phone) setSinglePhone(normalizeTzPhone(c.phone));
              }}
              disabled={singleState === "sending" || customers.length === 0}
            >
              <option value="">
                {customers.length === 0 ? "No customers loaded — type a phone" : "Pick a customer…"}
              </option>
              {customers.map((c) => (
                <option key={c.id} value={String(c.id)}>{customerLabel(c)}</option>
              ))}
            </select>
          </AppField>
          <AppField label="Phone number">
            <AppInput
              type="tel"
              placeholder="+255…"
              maxLength={16}
              value={singlePhone}
              onChange={(e) => setSinglePhone(normalizeTzPhone(e.target.value))}
              disabled={singleState === "sending"}
            />
          </AppField>
          <AppField label="Message">
            <AppTextarea
              value={singleMessage}
              onChange={(e) => setSingleMessage(e.target.value)}
              placeholder="Type your message…"
              disabled={singleState === "sending"}
              rows={4}
            />
          </AppField>
          {singleState === "error" && <span className="error" role="alert">{singleError}</span>}
          {singleState === "success" && (
            <p className="pg-text-muted"><IconCheck size={14} /> Message sent.</p>
          )}
          <div className="pg-modal-actions">
            <AppButton type="submit" disabled={singleState === "sending"}>
              {singleState === "sending" ? <><IconSpinner size={14} /> Sending…</> : "Send Message"}
            </AppButton>
          </div>
        </form>
      </AppCard>

      <AppCard title="Bulk broadcast">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (bulkPhoneList.length === 0 || !bulkMessage.trim()) {
              setBulkState("error");
              setBulkError("Pick customers or paste phone numbers, and enter a message");
              return;
            }
            if (invalidBulkPhones.length > 0) {
              setBulkState("error");
              setBulkError(`Invalid Tanzania mobile number${invalidBulkPhones.length === 1 ? "" : "s"}: ${invalidBulkPhones.join(", ")}`);
              return;
            }
            setConfirmBulk(true);
          }}
        >
          {customers.length > 0 && (
            <AppField label="Pick customers">
              <div style={{ maxHeight: 180, overflow: "auto", border: "1px solid var(--pg-border, #ddd)", borderRadius: 6, padding: 8 }}>
                {customers.slice(0, 100).map((c) => (
                  <label key={c.id} style={{ display: "block", marginBottom: 4, fontSize: 14 }}>
                    <input
                      type="checkbox"
                      checked={selectedBulkIds.includes(c.id)}
                      onChange={() => toggleBulkCustomer(c.id)}
                      disabled={bulkState === "sending"}
                    />{" "}
                    {customerLabel(c)}
                  </label>
                ))}
              </div>
            </AppField>
          )}
          <AppField label="Extra phone numbers (optional, one per line)">
            <AppTextarea
              value={bulkPhones}
              onChange={(e) => setBulkPhones(e.target.value)}
              placeholder={"+255700000001\n+255700000002"}
              disabled={bulkState === "sending"}
              rows={4}
            />
          </AppField>
          <p className="pg-text-muted">
            {bulkPhoneList.length} recipient{bulkPhoneList.length !== 1 ? "s" : ""}
          </p>
          <AppField label="Message">
            <AppTextarea
              value={bulkMessage}
              onChange={(e) => setBulkMessage(e.target.value)}
              placeholder="Type your broadcast message…"
              disabled={bulkState === "sending"}
              rows={4}
            />
          </AppField>
          {templates.length > 0 && (
            <div className="pg-filter-chips">
              {templates.map((t) => (
                <button
                  key={t.name}
                  type="button"
                  className="pg-chip"
                  onClick={() => applyTemplate(t, "bulk")}
                  title={t.template}
                >
                  {templateLabel(t.name)}
                </button>
              ))}
            </div>
          )}
          {bulkState === "error" && <span className="error" role="alert">{bulkError}</span>}
          {bulkState === "success" && bulkResult && (
            <p className="pg-text-muted">
              <IconCheck size={14} /> Sent to {bulkResult.sent} recipient{bulkResult.sent !== 1 ? "s" : ""}
              {bulkResult.failed > 0 ? `, ${bulkResult.failed} failed` : ""}.
            </p>
          )}
          <div className="pg-modal-actions">
            <AppButton type="submit" disabled={bulkState === "sending"}>
              {bulkState === "sending" ? <><IconSpinner size={14} /> Sending…</> : "Send Broadcast"}
            </AppButton>
          </div>
        </form>
      </AppCard>

      <ConfirmAction
        open={confirmBulk}
        title="Send bulk SMS?"
        message={`Broadcast to ${bulkPhoneList.length} recipient${bulkPhoneList.length === 1 ? "" : "s"}? Messages cannot be recalled.`}
        confirmLabel="Send broadcast"
        danger
        busy={bulkState === "sending"}
        onCancel={() => setConfirmBulk(false)}
        onConfirm={() => void sendBulk()}
      />
    </div>
  );
}
